import { graphql, useStaticQuery } from 'gatsby';

import { Helmet } from 'react-helmet';
import React from 'react';
import dayjs from 'dayjs';
import {
    useGlobalLocalization,
    usePageLocalization,
} from '@sbi-international/boutik.gatsby.plugin.multilocale';

interface ISeo {
    title: string;
    description: string;
    meta?: [];
    type?: string;
    schema?: { product: any; price: any; url: any; reviews: any };
}

function SEO({ title, description, meta, schema }: ISeo) {
    const { site } = useStaticQuery(
        graphql`
            query {
                site {
                    siteMetadata {
                        title
                        description
                    }
                }
            }
        `
    );

    const metaDescription = description;

    const { localizationContext } = useGlobalLocalization();
    const { currentPageAlternatives } = usePageLocalization();

    /* Schema related */
    const { product, price, reviews } = schema || {};

    const isDev =
        process.env.GATSBY_BUILD_TYPE === 'dev' ||
        process.env.GATSBY_BUILD_TYPE === 'staging';

    let reviewsAverage = 0;
    let reviewsCount = 0;
    if (schema?.reviews) {
        reviews.forEach((review: any) => {
            reviewsCount = reviewsCount + 1;
            reviewsAverage = reviewsAverage + review.average_rating;
        });
        reviewsAverage = (reviewsAverage / reviewsCount) * 0.05;
    }

    const websiteUrl = 'https://occanada.com';

    return (
        <Helmet
            htmlAttributes={{
                lang: localizationContext.currentLocale.bcp47LocaleCode,
            }}
            title={title}
            titleTemplate={`%s | ${site.siteMetadata.title}`}
            meta={[
                {
                    name: `description`,
                    content: metaDescription,
                },
                {
                    property: `og:title`,
                    content: title,
                },
                {
                    property: `og:description`,
                    content: metaDescription,
                },
                {
                    property: `og:type`,
                    content: `website`,
                },
                {
                    name: `twitter:card`,
                    content: `summary`,
                },
                {
                    name: `twitter:creator`,
                    content: site.siteMetadata.author,
                },
                {
                    name: `twitter:title`,
                    content: title,
                },
                {
                    name: `twitter:description`,
                    content: metaDescription,
                },
            ].concat(meta)}
        >
            {isDev && <meta name='robots' content='noindex, nofollow' />}
            <link
                rel='canonical'
                href={
                    websiteUrl +
                    currentPageAlternatives[
                        localizationContext.currentLocale.localeCode
                    ]
                }
            />
            {Object.keys(currentPageAlternatives).map((key) => {
                return (
                    <link
                        rel='alternate'
                        href={websiteUrl + currentPageAlternatives[key]}
                        hrefLang={
                            localizationContext.locales[key].bcp47LocaleCode
                        }
                        key={key}
                    />
                );
            })}

            {schema && (
                <script type='application/json'>
                    {`{
                    "@context": "https://schema.org/",
                    "@type": "Product",
                    "image": [${product.images.map(
                        (image: string) => `"${image}"`
                    )}],
                    "description": "${product?.description?.[
                        localizationContext.currentLocale.localeCode
                    ]
                        .split('\n')
                        .join(',')}",
                    "brand": {
                        "@type": "Brand",
                        "name": "occ"
                    },
                    "manufacturer": "SBI - Stove Builder International Inc.",
                    "model": "${product.code}",
                    "sku": "${product.code}",
                    "mpn": "${product.code}",
                    "name": "${
                        product.title[
                            localizationContext.currentLocale.localeCode
                        ]
                    }",
                    "category": "${product.category}",
                    "offers": {
                        "@type": "Offer",
                        "url": "https://www.occanada.com${
                            currentPageAlternatives[
                                localizationContext.currentLocale.localeCode
                            ]
                        }",
                        "priceCurrency": "${price.currency}",
                        "price": "${price.value}",
                        "itemCondition": "https://schema.org/NewCondition",
                        "availability": "https://schema.org/InStock"
                      },
                    "aggregateRating": {
                        "@type": "AggregateRating",
                        "ratingValue": "${reviewsAverage}",
                        "reviewCount": "${reviewsCount}"
                    },
                    "review" : [
                        ${reviews?.map((review: any) => {
                            return `{
                                "@type" : "Review",
                                "author": "${review.nickname}",
                                "datePublished": "${dayjs(
                                    review.created_at
                                ).format('YYYY-MM-DD')}",
                                "name": "${review.summary}",
                                "reviewRating": {
                                    "@type": "Rating",
                                    "ratingValue": "${
                                        review.average_rating * 0.05
                                    }"
                                }
                            }`;
                        })}
                    ]
                }`}
                </script>
            )}
        </Helmet>
    );
}

SEO.defaultProps = {
    meta: [],
    description: ``,
};

export default SEO;
